<template>
	<div class="content-wrapper">
		<div class="container flex-grow-1 container-p-y">

			<div class="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-6 row-gap-4">
				<div class="d-flex flex-column justify-content-center">
					<h4 class="mb-1">{{ $t('microLearning.assignmentDetail.title') }}</h4>
				</div>
				<div class="d-flex align-content-center flex-wrap gap-4">
					<router-link to="/micro-learning/assignments">
						<button type="button" class="btn btn-outline-secondary">
							<i class="ri-arrow-left-line me-1"></i>{{ $t('microLearning.assignmentDetail.back') }}
						</button>
					</router-link>
				</div>
			</div>

			<div v-if="assignment">
				<!-- Employee & Module Info -->
				<div class="row mb-6">
					<div class="col-lg-6 mb-6 mb-lg-0">
						<div class="card h-100">
							<div class="card-header">
								<h5 class="mb-0">{{ $t('microLearning.assignmentDetail.employeeInfo') }}</h5>
							</div>
							<div class="card-body">
								<div class="d-flex align-items-center mb-3">
									<div class="avatar me-3">
										<span class="avatar-initial rounded-circle bg-label-primary">{{ initials }}</span>
									</div>
									<div>
										<h6 class="mb-0">{{ assignment.user.firstName }} {{ assignment.user.lastName }}</h6>
										<small class="text-muted">{{ assignment.user.email }}</small>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="col-lg-6">
						<div class="card h-100">
							<div class="card-header d-flex justify-content-between align-items-center">
								<h5 class="mb-0">{{ $t('microLearning.assignmentDetail.moduleInfo') }}</h5>
								<span class="badge rounded-pill" :class="statusBadgeClass(assignment.status)">
									{{ statusLabel(assignment.status) }}
								</span>
							</div>
							<div class="card-body">
								<h6 class="mb-2">{{ assignment.module.name }}</h6>
								<p class="text-muted small mb-4" v-if="assignment.module.description">{{ assignment.module.description }}</p>

								<div v-if="assignment.status === 'completed'">
									<div class="d-flex justify-content-between mb-2">
										<span class="fw-medium">{{ $t('microLearning.assignments.score') }}</span>
										<span class="fw-bold" :class="scoreClass(assignment.score)">{{ assignment.score }}%</span>
									</div>
									<div class="progress" style="height: 8px;">
										<div
											class="progress-bar"
											:class="scoreBarClass(assignment.score)"
											role="progressbar"
											:style="{ width: assignment.score + '%' }"
											:aria-valuenow="assignment.score"
											aria-valuemin="0"
											aria-valuemax="100"
										></div>
									</div>
									<small class="text-muted mt-2 d-block">
										{{ assignment.correctAnswers }} / {{ assignment.totalQuestions }} {{ $t('microLearning.assignmentDetail.correct').toLowerCase() }}
									</small>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Scoring Suggestion -->
				<div class="alert mb-6 d-flex align-items-start gap-3" :class="scoringAlertClass" v-if="assignment.status === 'completed' && assignment.correctAnswers !== null">
					<span style="font-size: 20px;">{{ scoringIcon }}</span>
					<div>
						<strong class="d-block mb-1">{{ scoringTitle }}</strong>
						<span>{{ scoringText }}</span>
					</div>
				</div>

				<!-- Timeline -->
				<div class="card mb-6">
					<div class="card-header">
						<h5 class="mb-0">{{ $t('microLearning.assignmentDetail.timeline') }}</h5>
					</div>
					<div class="card-body">
						<ul class="timeline-list">
							<li class="timeline-item" :class="{ active: true }">
								<div class="timeline-dot bg-primary"></div>
								<div class="timeline-content">
									<div class="d-flex align-items-center gap-2 mb-1">
										<span class="fw-medium">{{ $t('microLearning.assignmentDetail.assignedAt') }}</span>
									</div>
									<span class="text-muted small">{{ formatDateTime(assignment.createdAt) }}</span>
									<p class="text-muted small mb-0 mt-1" v-if="assignment.assignedBy">
										Par {{ assignment.assignedBy.firstName }} {{ assignment.assignedBy.lastName }}
									</p>
								</div>
							</li>
							<li class="timeline-item active" v-if="assignment.startedAt">
								<div class="timeline-dot bg-info"></div>
								<div class="timeline-content">
									<div class="d-flex align-items-center gap-2 mb-1">
										<span class="fw-medium">{{ $t('microLearning.assignmentDetail.startedAt') }}</span>
									</div>
									<span class="text-muted small">{{ formatDateTime(assignment.startedAt) }}</span>
								</div>
							</li>
							<li class="timeline-item active" v-if="assignment.completedAt">
								<div class="timeline-dot bg-success"></div>
								<div class="timeline-content">
									<div class="d-flex align-items-center gap-2 mb-1">
										<span class="fw-medium">
											{{ $t('microLearning.assignmentDetail.completedAt') }} avec un score de <span :class="scoreClass(assignment.score)">{{ assignment.score }}%</span>
										</span>
									</div>
									<span class="text-muted small">{{ formatDateTime(assignment.completedAt) }}</span>
									<p class="text-muted small mb-0 mt-1" v-if="assignment.startedAt">
										{{ duration }}
									</p>
								</div>
							</li>
						</ul>
					</div>
				</div>

				<!-- Question Results -->
				<div class="card" v-if="assignment.status === 'completed' && answers && answers.length > 0">
					<div class="card-header d-flex justify-content-between align-items-center">
						<h5 class="mb-0">{{ $t('microLearning.assignmentDetail.questionResults') }}</h5>
						<span class="badge bg-label-primary rounded-pill">{{ answers.length }} questions</span>
					</div>
					<div class="card-body p-0">
						<div v-for="(answer, index) in answers" :key="answer.questionId" class="question-result-item" :class="{ 'border-bottom': index < answers.length - 1 }">
							<!-- Question header -->
							<div class="d-flex align-items-start gap-3 p-4 pb-3">
								<div class="question-number" :class="answer.isCorrect ? 'bg-success' : 'bg-danger'">
									{{ index + 1 }}
								</div>
								<div class="flex-grow-1">
									<h6 class="mb-1">{{ answer.question }}</h6>
									<div v-if="answer.isCorrect" class="d-flex align-items-center gap-2">
										<span class="result-chip result-chip-success">
											<i class="ri-check-line"></i>
											{{ $t('microLearning.assignmentDetail.correct') }}
										</span>
									</div>
								</div>
							</div>

							<!-- Answer options -->
							<div class="px-4 pb-4" style="padding-left: 4.25rem !important;">
								<div
									v-for="(option, oIndex) in answer.answers"
									:key="oIndex"
									class="answer-option"
									:class="getAnswerOptionClass(answer, option)"
								>
									<div class="answer-indicator">
										<i v-if="answer.validAnswers.includes(option) && answer.userReplies && answer.userReplies.includes(option)" class="ri-checkbox-circle-fill"></i>
										<i v-else-if="answer.validAnswers.includes(option)" class="ri-checkbox-circle-line"></i>
										<i v-else-if="answer.userReplies && answer.userReplies.includes(option)" class="ri-close-circle-fill"></i>
										<i v-else class="ri-checkbox-blank-circle-line"></i>
									</div>
									<span class="answer-text">{{ option }}</span>
									<span v-if="answer.userReplies && answer.userReplies.includes(option) && answer.validAnswers.includes(option)" class="answer-tag answer-tag-success">
										<i class="ri-check-line"></i> {{ $t('microLearning.assignmentDetail.correct') }}
									</span>
									<span v-else-if="answer.userReplies && answer.userReplies.includes(option) && !answer.validAnswers.includes(option)" class="answer-tag answer-tag-danger">
										<i class="ri-close-line"></i> {{ $t('microLearning.assignmentDetail.givenAnswer') }}
									</span>
									<span v-else-if="answer.validAnswers.includes(option)" class="answer-tag answer-tag-outline">
										{{ $t('microLearning.assignmentDetail.expectedAnswer') }}
									</span>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<!-- Loading -->
			<div v-if="isLoading" class="text-center py-5">
				<div class="spinner-border text-primary" role="status">
					<span class="visually-hidden">Loading...</span>
				</div>
			</div>
		</div>
		<div class="content-backdrop fade"></div>
	</div>
</template>

<script>
import HTTP from "@/axios";

export default {
	name: "MicroLearningAssignmentDetail",

	data() {
		return {
			assignment: null,
			answers: [],
			isLoading: true,
		};
	},

	computed: {
		initials() {
			if (!this.assignment || !this.assignment.user) return "";
			const f = this.assignment.user.firstName ? this.assignment.user.firstName.charAt(0).toUpperCase() : "";
			const l = this.assignment.user.lastName ? this.assignment.user.lastName.charAt(0).toUpperCase() : "";
			return `${f}${l}`;
		},
		duration() {
			if (!this.assignment?.startedAt || !this.assignment?.completedAt) return "";
			const start = new Date(this.assignment.startedAt);
			const end = new Date(this.assignment.completedAt);
			const diff = Math.round((end - start) / 1000);
			if (diff < 60) return `${diff}s`;
			const min = Math.floor(diff / 60);
			const sec = diff % 60;
			return `${min}min ${sec}s`;
		},
		scoringRatio() {
			if (!this.assignment?.totalQuestions) return 0;
			return this.assignment.correctAnswers / this.assignment.totalQuestions;
		},
		scoringAlertClass() {
			if (this.scoringRatio >= 1) return "alert-success";
			if (this.scoringRatio >= 0.8) return "alert-warning";
			return "alert-danger";
		},
		scoringIcon() {
			if (this.scoringRatio >= 1) return "✅";
			if (this.scoringRatio >= 0.8) return "⚠️";
			return "🔄";
		},
		scoringTitle() {
			if (this.scoringRatio >= 1) return this.$t("microLearning.assignmentDetail.scoringConfirmed");
			if (this.scoringRatio >= 0.8) return this.$t("microLearning.assignmentDetail.scoringBroad");
			return this.$t("microLearning.assignmentDetail.scoringRefresher");
		},
		scoringText() {
			if (this.scoringRatio >= 1) return this.$t("microLearning.assignmentDetail.scoringConfirmedDesc");
			if (this.scoringRatio >= 0.8) return this.$t("microLearning.assignmentDetail.scoringBroadDesc");
			return this.$t("microLearning.assignmentDetail.scoringRefresherDesc");
		}
	},

	methods: {
		async fetchAssignment() {
			this.isLoading = true;
			try {
				const response = await HTTP.get(`/micro-learning/assignments/${this.$route.params.id}`);
				this.assignment = response.data.assignment;
				this.answers = response.data.answers || [];
			} catch (error) {
				const errorMessage = error?.response?.data?.message || this.$t("errors.generic");
				alert(errorMessage);
				console.error("Erreur API:", error);
				this.$router.push("/micro-learning/assignments");
			} finally {
				this.isLoading = false;
			}
		},

		statusBadgeClass(status) {
			const map = { pending: "bg-label-primary", started: "bg-label-info", completed: "bg-label-success" };
			return map[status] || "bg-label-secondary";
		},

		statusLabel(status) {
			return this.$t(`microLearning.assignments.${status}`);
		},

		scoreClass(score) {
			if (score <= 40) return "text-danger";
			if (score <= 70) return "text-warning";
			return "text-success";
		},

		scoreBarClass(score) {
			if (score <= 40) return "bg-danger";
			if (score <= 70) return "bg-warning";
			return "bg-success";
		},

		getAnswerOptionClass(answer, option) {
			const isValid = answer.validAnswers.includes(option);
			const isUserReply = answer.userReplies && answer.userReplies.includes(option);

			if (isValid && isUserReply) return "answer-correct-chosen";
			if (!isValid && isUserReply) return "answer-wrong-chosen";
			if (isValid && !isUserReply) return "answer-correct-missed";
			return "answer-neutral";
		},

		formatDateTime(dateStr) {
			if (!dateStr) return "-";
			const d = new Date(dateStr);
			return d.toLocaleDateString("fr-FR", {
				day: "2-digit",
				month: "2-digit",
				year: "numeric",
				hour: "2-digit",
				minute: "2-digit",
			});
		},
	},

	async mounted() {
		await this.fetchAssignment();
	},
};
</script>

<style scoped>
/* ── TIMELINE ── */
.timeline-list {
	list-style: none;
	padding: 0;
	margin: 0;
	position: relative;
}

.timeline-item {
	position: relative;
	padding-left: 32px;
	padding-bottom: 24px;
}

.timeline-item:last-child {
	padding-bottom: 0;
}

/* Vertical line */
.timeline-item:not(:last-child)::before {
	content: '';
	position: absolute;
	left: 7px;
	top: 18px;
	bottom: 0;
	width: 2px;
	background: #e0e0e0;
}

.timeline-item.active:not(:last-child)::before {
	background: #c8c8c8;
}

/* Dot */
.timeline-dot {
	position: absolute;
	left: 0;
	top: 4px;
	width: 16px;
	height: 16px;
	border-radius: 50%;
	border: 3px solid #fff;
	box-shadow: 0 0 0 1px rgba(0,0,0,.08);
}

.timeline-content {
	min-height: 24px;
}

/* ── QUESTION RESULTS ── */
.question-number {
	width: 32px;
	height: 32px;
	border-radius: 10px;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #fff;
	font-weight: 700;
	font-size: 14px;
	flex-shrink: 0;
}

.result-chip {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	padding: 2px 10px;
	border-radius: 20px;
	font-size: 12px;
	font-weight: 600;
}

.result-chip-success {
	background: rgba(40, 199, 111, 0.12);
	color: #28c76f;
}

.result-chip-danger {
	background: rgba(234, 84, 85, 0.12);
	color: #ea5455;
}

/* Answer options */
.answer-option {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 10px 14px;
	border-radius: 10px;
	margin-bottom: 6px;
	transition: all .15s ease;
	border: 1px solid transparent;
}

.answer-option:last-child {
	margin-bottom: 0;
}

.answer-indicator {
	font-size: 18px;
	line-height: 1;
	flex-shrink: 0;
}

.answer-text {
	flex: 1;
	font-size: 14px;
}

/* Answer states */
.answer-correct-chosen {
	background: rgba(40, 199, 111, 0.08);
	border-color: rgba(40, 199, 111, 0.2);
}
.answer-correct-chosen .answer-indicator { color: #28c76f; }
.answer-correct-chosen .answer-text { font-weight: 500; color: #1e7e34; }

.answer-wrong-chosen {
	background: rgba(234, 84, 85, 0.08);
	border-color: rgba(234, 84, 85, 0.2);
}
.answer-wrong-chosen .answer-indicator { color: #ea5455; }
.answer-wrong-chosen .answer-text { font-weight: 500; color: #c0392b; text-decoration: line-through; }

.answer-correct-missed {
	background: rgba(40, 199, 111, 0.04);
	border-color: rgba(40, 199, 111, 0.12);
}
.answer-correct-missed .answer-indicator { color: #28c76f; opacity: 0.6; }
.answer-correct-missed .answer-text { color: #666; }

.answer-neutral {
	background: transparent;
}
.answer-neutral .answer-indicator { color: #c8c8c8; }
.answer-neutral .answer-text { color: #999; }

/* Answer tags */
.answer-tag {
	display: inline-flex;
	align-items: center;
	gap: 3px;
	padding: 2px 8px;
	border-radius: 6px;
	font-size: 11px;
	font-weight: 600;
	flex-shrink: 0;
	white-space: nowrap;
}

.answer-tag-success {
	background: rgba(40, 199, 111, 0.15);
	color: #28c76f;
}

.answer-tag-danger {
	background: rgba(234, 84, 85, 0.15);
	color: #ea5455;
}

.answer-tag-outline {
	background: rgba(40, 199, 111, 0.08);
	color: #28c76f;
	border: 1px solid rgba(40, 199, 111, 0.2);
}

/* Question separator */
.question-result-item {
	transition: background .15s ease;
}
.question-result-item:hover {
	background: rgba(0,0,0,.01);
}
</style>
